// drw-nav-analytics.js — theme-side analytics bridge for the navigation A/B tests.
//
// The store's GA4 is wired through the Google & YouTube app, which runs in a
// Web Pixel sandbox with no access to the storefront DOM. So nav clicks and
// collection-card impressions cannot be observed from the pixel directly.
//
// This script lives in the theme (it can see the DOM), captures those signals,
// and re-publishes them as custom events via Shopify.analytics.publish(). A
// custom Web Pixel subscribes to them (analytics.subscribe) and forwards them
// to GA4 as select_item / view_item_list with the documented params.
//
// Consent: publishing here is harmless; the receiving pixel is responsible for
// gating delivery on the visitor's analytics consent (Customer Privacy API).
//
// Covers: DRW-89 (nav-click + collection impressions) and the code part of
// DRW-96 (page_type, entry_nav). experiment_variant is populated by the A/B
// tool (Shogun, DRW-90) — not here.

(function () {
  'use strict';

  // Don't track inside the theme editor — interactions there are not real users.
  if (window.Shopify && window.Shopify.designMode) return;

  var ENTRY_NAV_KEY = 'drw_entry_nav';

  // Thin wrapper around Shopify.analytics.publish with a hard guard, so a
  // missing analytics object never throws on the storefront.
  function publish(eventName, payload) {
    try {
      if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
        window.Shopify.analytics.publish(eventName, payload || {});
      }
    } catch (e) {
      /* never let tracking break the page */
    }
  }

  // ---------------------------------------------------------------------------
  // DRW-96 — page_type
  // Source: <body class="template-{{ request.page_type }}"> (set in theme.liquid).
  // Normalised to the four buckets GA4 expects, everything else -> 'other'.
  // ---------------------------------------------------------------------------
  function resolvePageType() {
    var body = document.body;
    if (!body) return 'other';
    var cls = body.className || '';
    if (/\btemplate-index\b/.test(cls)) return 'homepage';
    if (/\btemplate-collection\b/.test(cls) || /\btemplate-list-collections\b/.test(cls)) return 'collection';
    if (/\btemplate-product\b/.test(cls)) return 'product';
    if (/\btemplate-article\b/.test(cls) || /\btemplate-blog\b/.test(cls)) return 'article';
    return 'other';
  }

  function emitPageContext() {
    publish('drw_page_context', {
      page_type: resolvePageType(),
      entry_nav: sessionEntryNav()
    });
  }

  function sessionEntryNav() {
    try {
      return window.sessionStorage.getItem(ENTRY_NAV_KEY) || null;
    } catch (e) {
      return null;
    }
  }

  // ---------------------------------------------------------------------------
  // DRW-89 — nav clicks (+ DRW-96 entry_nav first-touch)
  // Event delegation from document, so we don't have to edit each nav snippet.
  // ---------------------------------------------------------------------------
  function navLocation(anchor) {
    if (anchor.closest('.meganav__nav')) return 'mega';
    // Secondary nav: desktop is wrapped in .header__links-secondary /
    // [data-secondary-navigation] (header.liquid), mobile in
    // .drawer-menu__secondary-links. Must be checked before the primary
    // fallback, which would otherwise also match their inner .header__links-list.
    if (anchor.closest('.header__links-secondary, [data-secondary-navigation], .drawer-menu__secondary-links')) return 'secondary';
    if (anchor.closest('.drawer-menu')) return 'drawer';
    if (anchor.closest('.header__links-list')) return 'primary';
    return null;
  }

  function onNavClick(evt) {
    var anchor = evt.target.closest && evt.target.closest('a[href]');
    if (!anchor) return;

    var location = navLocation(anchor);
    if (!location) return; // not a navigation link

    var label = (anchor.textContent || '').replace(/\s+/g, ' ').trim();
    var destination = anchor.getAttribute('href');

    // entry_nav: remember the FIRST nav click of the session.
    if (!sessionEntryNav()) {
      try { window.sessionStorage.setItem(ENTRY_NAV_KEY, label || destination); } catch (e) {}
    }

    publish('drw_nav_click', {
      nav_location: location,
      nav_item_label: label,
      nav_destination: destination
    });
  }

  // ---------------------------------------------------------------------------
  // DRW-89 — collection-card impressions (view_item_list) + select (select_item)
  // Reads item data from data-* on .product-item (added in product-item.liquid).
  // Impressions are batched per animation frame so one scroll = one event.
  // ---------------------------------------------------------------------------
  // Position of a card within its list/section (1-based). Computed from the DOM
  // because forloop.index is not available inside a {% render %} scope (the same
  // reason the theme's own --z-index-item is unset on the collection grid), so a
  // server-side data-position attribute would always be empty.
  function cardIndex(card) {
    var scope = card.closest('[data-section-type], .shopify-section') || document;
    var cards = scope.querySelectorAll('.product-item[data-product-id]');
    for (var i = 0; i < cards.length; i++) {
      if (cards[i] === card) return i + 1;
    }
    return null;
  }

  function itemFromCard(card, index) {
    return {
      item_id: card.getAttribute('data-product-id') || null,
      item_name: card.getAttribute('data-product-title') || null,
      price: card.getAttribute('data-product-price') || null,
      index: index != null ? index + 1 : cardIndex(card)
    };
  }

  function listNameFor(card) {
    // Use the collection/section context as the list name when available.
    var section = card.closest('[data-section-type], .shopify-section');
    return (section && (section.getAttribute('data-list-name') || document.title)) || 'collection';
  }

  function initImpressions() {
    if (!('IntersectionObserver' in window)) return;

    var cards = document.querySelectorAll('.product-item[data-product-id]');
    if (!cards.length) return;

    var batch = [];
    var flushScheduled = false;

    function flush() {
      flushScheduled = false;
      if (!batch.length) return;
      publish('drw_view_item_list', {
        item_list_name: listNameFor(batch[0].el),
        items: batch.map(function (b) { return b.item; })
      });
      batch = [];
    }

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var card = entry.target;
        observer.unobserve(card); // count each card once
        batch.push({ el: card, item: itemFromCard(card) });
        if (!flushScheduled) {
          flushScheduled = true;
          window.requestAnimationFrame(flush);
        }
      });
    }, { threshold: 0.5 });

    cards.forEach(function (card) { observer.observe(card); });
  }

  function onCardClick(evt) {
    var anchor = evt.target.closest && evt.target.closest('.product-item a[href]');
    if (!anchor) return;
    var card = anchor.closest('.product-item[data-product-id]');
    if (!card) return;
    publish('drw_select_item', {
      item_list_name: listNameFor(card),
      items: [itemFromCard(card)]
    });
  }

  // ---------------------------------------------------------------------------
  // Block 3 — cart cross-sell ("weekly offer", e.g. Ketosüß) — native
  // cross-sells.liquid in the cart drawer. Same item-payload as collection cards.
  // The drawer is hidden on load, so IntersectionObserver fires when it opens;
  // we also re-scan on cart update for freshly rendered items.
  // ---------------------------------------------------------------------------
  var crossSellObserver = null;

  function initCrossSellImpressions() {
    if (!('IntersectionObserver' in window)) return;
    if (!crossSellObserver) {
      crossSellObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var el = entry.target;
          crossSellObserver.unobserve(el);
          publish('drw_view_item_list', {
            item_list_name: 'cart_cross_sell',
            items: [itemFromCard(el)]
          });
        });
      }, { threshold: 0.5 });
    }
    document.querySelectorAll('.cross-sells__item[data-product-id]').forEach(function (el) {
      if (el.__drwObserved) return;
      el.__drwObserved = true;
      crossSellObserver.observe(el);
    });
  }

  function onCrossSellClick(evt) {
    var el = evt.target.closest && evt.target.closest('.cross-sells__item[data-product-id]');
    if (!el) return;
    var isAdd = !!evt.target.closest('.cross-sells__item-add');
    publish('drw_select_item', {
      item_list_name: 'cart_cross_sell',
      action: isAdd ? 'add' : 'open',
      items: [itemFromCard(el)]
    });
  }

  // ---------------------------------------------------------------------------
  // Block 1 + cart offer — Candy Rack upsell ("Mehr anzeigen" / weekly offer).
  // Candy Rack is a third-party app (shopify://apps/candy-rack) but it renders
  // into the normal page DOM (NOT an iframe) — confirmed from live HTML:
  //   - PDP "Für noch bessere Ergebnisse"  -> .candyrack-ppp-checkbox-layout
  //       show-more ("Mehr anzeigen")       -> .candyrack-ppp-checkbox-layout__show-more-button
  //   - cart weekly offer ("…geschenkt")    -> #candyrack-slider-cart (an ID, the
  //       container's class is empty — confirmed live 2026-06-11. Inner offer items,
  //       once the slider populates, carry candyrack-* classes caught by the catch-all.)
  // Delegated listener; harmless no-op if the app markup ever changes.
  // ---------------------------------------------------------------------------
  var CANDY_RACK_ROOT = '.candyrack-ppp-checkbox-layout, #candyrack-slider-cart, .candyrack-slider-cart, [class*="candyrack"]';
  var CANDY_RACK_SHOW_MORE = '.candyrack-ppp-checkbox-layout__show-more-button, [class*="show-more"]';

  function onCandyRackClick(evt) {
    var root = evt.target.closest && evt.target.closest(CANDY_RACK_ROOT);
    if (!root) return;
    var isShowMore = !!evt.target.closest(CANDY_RACK_SHOW_MORE);
    var inCart = !!evt.target.closest('#candyrack-slider-cart, .candyrack-slider-cart, .cross-sells, .quick-cart, cart-drawer');
    publish('drw_candy_rack_click', {
      placement: inCart ? 'cart' : 'product',
      action: isShowMore ? 'show_more' : 'other',
      label: (evt.target.textContent || '').replace(/\s+/g, ' ').trim().slice(0, 80)
    });
  }

  // ---------------------------------------------------------------------------
  // Wire-up
  // ---------------------------------------------------------------------------
  function init() {
    emitPageContext();
    document.addEventListener('click', onNavClick, true);
    document.addEventListener('click', onCardClick, true);
    document.addEventListener('click', onCrossSellClick, true);
    document.addEventListener('click', onCandyRackClick, true);
    initImpressions();
    initCrossSellImpressions();

    // Cart drawer renders/updates cross-sells dynamically (Stiletto custom events).
    document.addEventListener('quick-cart:open', initCrossSellImpressions);
    document.addEventListener('cart:updated', initCrossSellImpressions);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
